import React, { useState, useEffect } from 'react';
import { Table, Icon, Header, Segment, Loader, Message, Label, Dropdown, } from 'semantic-ui-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useIsSuperAdmin } from '../hooks/useIsSuperAdmin';
import { FluidAdminClient } from './fluidAdmin.client';
const fluidAdminClient = new FluidAdminClient();
function formatDate(timestamp) {
    if (!timestamp)
        return '-';
    return new Date(timestamp).toLocaleDateString();
}
function formatDateTime(timestamp) {
    if (!timestamp)
        return '-';
    return new Date(timestamp).toLocaleString();
}
const FluidAdminPage = () => {
    const { isLoaded: authLoaded, isSignedIn } = useAuth();
    const { isLoaded: orgLoaded } = useOrganization();
    const isSuperAdmin = useIsSuperAdmin();
    const [companies, setCompanies] = useState([]);
    const [weeklyStats, setWeeklyStats] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [loading, setLoading] = useState(false);
    const [chartLoading, setChartLoading] = useState(false);
    const [error, setError] = useState('');
    if (!authLoaded || !orgLoaded) {
        return (React.createElement(Layout, null,
            React.createElement(Segment, { style: { padding: '50px', textAlign: 'center' } },
                React.createElement(Loader, { active: true, inline: "centered" }, "Loading..."))));
    }
    if (!isSuperAdmin) {
        return (React.createElement(Layout, null,
            React.createElement(Segment, { placeholder: true, style: { margin: '50px' } },
                React.createElement(Header, { icon: true },
                    React.createElement(Icon, { name: "lock" }),
                    "Access Denied"),
                React.createElement("p", null, "This page is only accessible to super administrators."))));
    }
    useEffect(() => {
        if (authLoaded && orgLoaded && isSignedIn) {
            loadCompanyStats();
            loadWeeklyStats();
        }
    }, [authLoaded, orgLoaded, isSignedIn]);
    useEffect(() => {
        loadWeeklyStats(selectedOrgId || undefined);
    }, [selectedOrgId]);
    const loadCompanyStats = async () => {
        try {
            setLoading(true);
            setError('');
            const stats = await fluidAdminClient.getCompanyStats();
            setCompanies(stats);
        }
        catch (err) {
            setError(err.message || 'Failed to load company statistics');
            console.error('Error loading company stats:', err);
        }
        finally {
            setLoading(false);
        }
    };
    const loadWeeklyStats = async (orgId) => {
        try {
            setChartLoading(true);
            const stats = await fluidAdminClient.getWeeklyStats(orgId);
            setWeeklyStats(stats);
        }
        catch (err) {
            console.error('Error loading weekly stats:', err);
        }
        finally {
            setChartLoading(false);
        }
    };
    const totalForms = companies.reduce((sum, c) => sum + c.forms.length, 0);
    const totalSubmissions = companies.reduce((sum, c) => sum + c.totalSubmissions, 0);
    const companyOptions = [
        { key: 'all', value: '', text: 'All Companies' },
        ...companies.map(c => ({ key: c.orgId, value: c.orgId, text: c.orgName })),
    ];
    return (React.createElement("div", { className: "p-6" },
        React.createElement(Segment, null,
            React.createElement("div", { className: "flex items-center justify-between mb-4" },
                React.createElement(Header, { as: "h3", style: { margin: 0 } },
                    React.createElement(Icon, { name: "chart bar" }),
                    React.createElement(Header.Content, null, "Weekly Activity")),
                React.createElement(Dropdown, { selection: true, placeholder: "Filter by company", options: companyOptions, value: selectedOrgId, onChange: (_, { value }) => setSelectedOrgId(value), style: { minWidth: '250px' } })),
            chartLoading ? (React.createElement(Loader, { active: true, inline: "centered" }, "Loading chart...")) : weeklyStats.length === 0 ? (React.createElement(Segment, { placeholder: true },
                React.createElement(Header, { icon: true },
                    React.createElement(Icon, { name: "chart bar outline" }),
                    "No weekly data available"))) : (React.createElement(ResponsiveContainer, { width: "100%", height: 350 },
                React.createElement(BarChart, { data: weeklyStats },
                    React.createElement(CartesianGrid, { strokeDasharray: "3 3" }),
                    React.createElement(XAxis, { dataKey: "week", tick: { fontSize: 12 } }),
                    React.createElement(YAxis, { allowDecimals: false }),
                    React.createElement(Tooltip, null),
                    React.createElement(Legend, null),
                    React.createElement(Bar, { dataKey: "submissions", name: "Submissions", fill: "#4183c4" }),
                    React.createElement(Bar, { dataKey: "formsCreated", name: "Forms Created", fill: "#21ba45" }),
                    React.createElement(Bar, { dataKey: "formsPublished", name: "Forms Published", fill: "#f2711c" }))))),
        React.createElement(Segment, null,
            React.createElement(Header, { as: "h2" },
                React.createElement(Icon, { name: "dashboard" }),
                React.createElement(Header.Content, null,
                    "Fluid Admin",
                    React.createElement(Header.Subheader, null, "Cross-company statistics and management"))),
            React.createElement("div", { className: "flex gap-4 mb-4" },
                React.createElement(Label, { size: "large" },
                    React.createElement(Icon, { name: "building" }),
                    companies.length,
                    " Companies"),
                React.createElement(Label, { size: "large" },
                    React.createElement(Icon, { name: "wpforms" }),
                    totalForms,
                    " Forms"),
                React.createElement(Label, { size: "large" },
                    React.createElement(Icon, { name: "file text" }),
                    totalSubmissions,
                    " Submissions")),
            error && (React.createElement(Message, { error: true, onDismiss: () => setError('') },
                React.createElement(Message.Header, null, "Error"),
                React.createElement("p", null, error))),
            loading && React.createElement(Loader, { active: true }, "Loading company statistics..."),
            !loading && companies.length === 0 && (React.createElement(Segment, { placeholder: true },
                React.createElement(Header, { icon: true },
                    React.createElement(Icon, { name: "building outline" }),
                    "No companies found"))),
            !loading && companies.length > 0 && (React.createElement(Table, { celled: true, striped: true },
                React.createElement(Table.Header, null,
                    React.createElement(Table.Row, null,
                        React.createElement(Table.HeaderCell, null, "Company Name"),
                        React.createElement(Table.HeaderCell, null, "Company ID"),
                        React.createElement(Table.HeaderCell, null, "Created"),
                        React.createElement(Table.HeaderCell, null, "User Email"),
                        React.createElement(Table.HeaderCell, null, "Account Created"),
                        React.createElement(Table.HeaderCell, null, "Last Login"),
                        React.createElement(Table.HeaderCell, null, "Forms"),
                        React.createElement(Table.HeaderCell, null, "Total Submissions"),
                        React.createElement(Table.HeaderCell, null, "Submissions per Form"))),
                React.createElement(Table.Body, null, companies.map((company) => {
                    const rowSpan = Math.max(company.users.length, 1);
                    return company.users.length > 0 ? (company.users.map((user, userIdx) => (React.createElement(Table.Row, { key: `${company.orgId}-${userIdx}` },
                        userIdx === 0 && (React.createElement(React.Fragment, null,
                            React.createElement(Table.Cell, { rowSpan: rowSpan },
                                React.createElement("strong", null, company.orgName)),
                            React.createElement(Table.Cell, { rowSpan: rowSpan },
                                React.createElement("code", { className: "text-xs" }, company.orgId)),
                            React.createElement(Table.Cell, { rowSpan: rowSpan }, formatDate(company.orgCreatedAt)))),
                        React.createElement(Table.Cell, null, user.email),
                        React.createElement(Table.Cell, null, formatDate(user.createdAt)),
                        React.createElement(Table.Cell, null, formatDateTime(user.lastSignInAt)),
                        userIdx === 0 && (React.createElement(React.Fragment, null,
                            React.createElement(Table.Cell, { rowSpan: rowSpan },
                                React.createElement(Label, { circular: true }, company.forms.length)),
                            React.createElement(Table.Cell, { rowSpan: rowSpan },
                                React.createElement(Label, { circular: true, color: company.totalSubmissions > 0 ? 'blue' : undefined }, company.totalSubmissions)),
                            React.createElement(Table.Cell, { rowSpan: rowSpan }, company.forms.length === 0 ? (React.createElement("span", { className: "text-gray-400" }, "No forms")) : (React.createElement("div", { className: "flex flex-col gap-1" }, company.forms.map((form) => (React.createElement("div", { key: form.formKey, className: "flex items-center justify-between gap-2" },
                                React.createElement(Link, { to: `/form-config/${form.formKey}?companyId=${company.orgId}&companyName=${encodeURIComponent(company.orgName)}`, className: "text-blue-600 hover:underline" }, form.name),
                                React.createElement(Label, { size: "mini", circular: true }, form.submissionCount))))))))))))) : (React.createElement(Table.Row, { key: company.orgId },
                        React.createElement(Table.Cell, null,
                            React.createElement("strong", null, company.orgName)),
                        React.createElement(Table.Cell, null,
                            React.createElement("code", { className: "text-xs" }, company.orgId)),
                        React.createElement(Table.Cell, null, formatDate(company.orgCreatedAt)),
                        React.createElement(Table.Cell, { className: "text-gray-400" }, "-"),
                        React.createElement(Table.Cell, { className: "text-gray-400" }, "-"),
                        React.createElement(Table.Cell, { className: "text-gray-400" }, "-"),
                        React.createElement(Table.Cell, null,
                            React.createElement(Label, { circular: true }, company.forms.length)),
                        React.createElement(Table.Cell, null,
                            React.createElement(Label, { circular: true, color: company.totalSubmissions > 0 ? 'blue' : undefined }, company.totalSubmissions)),
                        React.createElement(Table.Cell, null, company.forms.length === 0 ? (React.createElement("span", { className: "text-gray-400" }, "No forms")) : (React.createElement("div", { className: "flex flex-col gap-1" }, company.forms.map((form) => (React.createElement("div", { key: form.formKey, className: "flex items-center justify-between gap-2" },
                            React.createElement(Link, { to: `/form-config/${form.formKey}?companyId=${company.orgId}&companyName=${encodeURIComponent(company.orgName)}`, className: "text-blue-600 hover:underline" }, form.name),
                            React.createElement(Label, { size: "mini", circular: true }, form.submissionCount)))))))));
                })))))));
};
export default FluidAdminPage;
